import { router } from '@inertiajs/react';
import { X } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface RemoveHackerButtonProps {
  eventSlug: string;
  hackerProfileId: string;
  hackerName: string;
  teamId: string;
}

export function RemoveHackerButton({
  eventSlug,
  hackerProfileId,
  hackerName,
  teamId,
}: RemoveHackerButtonProps) {
  const [isPending, setIsPending] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const handleRemove = () => {
    setIsPending(true);
    router.delete(
      `/admin/${eventSlug}/teams/${teamId}/members/${hackerProfileId}`,
      {
        preserveScroll: true,
        onSuccess: () => {
          toast({
            type: 'success',
            description: `${hackerName} removed from team successfully`,
          });
          setShowDialog(false);
        },
        onError: (errors) => {
          const failure = errors as unknown as Record<
            string,
            string | undefined
          >;
          toast({
            type: 'error',
            description: failure.base ?? 'Failed to remove hacker from team',
          });
        },
        onFinish: () => setIsPending(false),
      },
    );
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowDialog(true)}
        disabled={isPending}
      >
        <X className="h-4 w-4 text-red-500" />
      </Button>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {hackerName} from this team? They
              will no longer be associated with this team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              disabled={isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {isPending ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
